import { useEffect, useRef } from 'react';

/**
 * Procedural voice-wave animation inspired by Milkinside's Dribbble shot.
 * Multiple sine-wave ribbons with drifting frequency, phase & amplitude,
 * painted with soft gradient strokes on a HiDPI-aware canvas.
 *
 * Props:
 *   size        "sm" | "md" | "lg" | "xl"     (default "md")
 *   colors      ["#hex", ...]                 (default amber → violet brand palette)
 *   speed       number                         (default 1 — wave drift speed)
 *   lineCount   number                         (default 5)
 *   className   string                         (extra Tailwind classes for wrapper)
 *   centerDot   boolean                        (default false — pulse dot at center)
 */
export default function VoiceWave({
  size = 'md',
  colors = ['#fbbf24', '#f59e0b', '#a78bfa', '#8b5cf6'],
  speed = 1,
  lineCount = 5,
  className = '',
  centerDot = false,
}) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startRef = useRef(performance.now());

  const dimensions = {
    sm: { w: 120, h: 40 },
    md: { w: 280, h: 80 },
    lg: { w: 520, h: 140 },
    xl: { w: 900, h: 220 },
  }[size] || { w: 280, h: 80 };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.max(window.devicePixelRatio || 1, 1);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = (now) => {
      const rect = canvas.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      const t = ((now - startRef.current) / 1000) * speed;

      ctx.clearRect(0, 0, W, H);

      const midY = H / 2;
      const maxAmp = H * 0.42;

      // Multiple sine-wave layers — each with unique freq, phase, amp drift
      for (let i = 0; i < lineCount; i++) {
        const layerT = t + i * 0.7;
        const freqBase = 0.006 + i * 0.0018;
        const freq = freqBase + Math.sin(layerT * 0.25) * 0.0008;
        const phase = layerT * (0.9 + i * 0.15);
        // Amplitude breathes slowly + falls off at layer edges
        const ampScale = (1 - i / (lineCount + 1)) * (0.55 + 0.45 * Math.sin(layerT * 0.55 + i));
        const amp = maxAmp * ampScale;

        // Build path — waves travel left → right by subtracting phase over time
        ctx.beginPath();
        const step = 2;
        for (let x = 0; x <= W; x += step) {
          // Edge attenuation so waves taper at the ends (ribbon look)
          const edge = Math.sin((x / W) * Math.PI);
          // Two superposed sines for organic shape; negative phase → left-to-right travel
          const y =
            midY +
            edge *
              (Math.sin(x * freq - phase) * amp * 0.7 +
                Math.sin(x * freq * 2.3 - phase * 1.5 + i) * amp * 0.3);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }

        // Horizontal gradient stroke that cycles through the palette,
        // with transparent endpoints so the line fades in/out smoothly at both ends.
        const grad = ctx.createLinearGradient(0, 0, W, 0);
        const offset = (layerT * 0.08) % 1;
        const toRGBA = (hex, a) => {
          const h = hex.replace('#', '');
          const r = parseInt(h.substring(0, 2), 16);
          const g = parseInt(h.substring(2, 4), 16);
          const b = parseInt(h.substring(4, 6), 16);
          return `rgba(${r},${g},${b},${a})`;
        };
        // Transparent at 0, ramp up by 18%, hold, ramp down by 82%, transparent at 100%
        grad.addColorStop(0, toRGBA(colors[0], 0));
        grad.addColorStop(0.08, toRGBA(colors[0], 0.2));
        const inner = 6; // interior color stops
        for (let s = 0; s <= inner; s++) {
          const pos = 0.18 + (0.64 * s) / inner;
          const colorIdx = Math.floor(((s / inner + offset) % 1) * colors.length);
          grad.addColorStop(pos, toRGBA(colors[colorIdx], 1));
        }
        grad.addColorStop(0.92, toRGBA(colors[colors.length - 1], 0.2));
        grad.addColorStop(1, toRGBA(colors[colors.length - 1], 0));

        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5 + (lineCount - i) * 0.4;
        ctx.globalAlpha = 0.35 + (1 - i / lineCount) * 0.55;
        ctx.lineCap = 'round';
        ctx.shadowColor = colors[i % colors.length];
        ctx.shadowBlur = 12;
        ctx.stroke();
      }

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;

      // Optional pulsing center dot
      if (centerDot) {
        const pulse = 0.6 + 0.4 * Math.sin(t * 2.2);
        ctx.beginPath();
        ctx.arc(W / 2, midY, 3 + pulse * 3, 0, Math.PI * 2);
        ctx.fillStyle = colors[0];
        ctx.shadowColor = colors[0];
        ctx.shadowBlur = 20;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [speed, lineCount, colors, centerDot]);

  return (
    <div
      className={`relative ${className}`}
      style={{ width: dimensions.w, height: dimensions.h }}
      data-testid="voice-wave"
    >
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
